/* eslint-disable @typescript-eslint/no-explicit-any */
/* Employee — Benefits & Deductions. Every item uses progressive disclosure:
   only an Applicable / Not Applicable toggle shows until enabled.

   Preserves the existing Noida-PT business rule — when Professional Tax is
   applicable, Noida PT is force-cleared and disabled with an explanatory note
   (not silently disabled). */
import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import Input from "components/Input";
import { preventSpecialCharKeyDown } from "util/preventSpecialCharInput";
import { SectionCard, ApplicableToggle, CheckboxField, HelperText } from "./_controls";

const EmployeeDeductionsSection = () => {
  const { watch, setValue } = useFormContext();
  const profTaxApplicable = watch("profTaxApplicable");

  // Existing rule: Noida PT cannot be enabled while Professional Tax is on.
  useEffect(() => {
    if (profTaxApplicable) {
      setValue("noidaPtApplicable", false, { shouldDirty: true, shouldValidate: true });
    }
  }, [profTaxApplicable, setValue]);

  return (
    <SectionCard title="Benefits & Deductions" subtitle="Enable only what applies to this structure.">
      <div className="divide-y divide-slate-100 dark:divide-gray-700">
        {/* Medical Insurance (benefit) */}
        <ApplicableToggle
          name="medicalInsuranceApplicable"
          label="Medical Insurance"
          subtitle="Employer-paid premium."
        >
          <div className="max-w-xs">
            <Input
              compact
              name="medicalInsuranceMonthly"
              label="Monthly Premium (₹)"
              type="number"
              onKeyDown={preventSpecialCharKeyDown}
            />
          </div>
        </ApplicableToggle>

        {/* Provident Fund */}
        <ApplicableToggle
          name="pfApplicable"
          label="Provident Fund"
          subtitle="Deducted from basic salary and matched by the employer."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 max-w-lg">
            <Input
              compact
              name="providentFundEmployeePercentage"
              label="Employee Contribution (%)"
              type="number"
              onKeyDown={preventSpecialCharKeyDown}
            />
            <Input
              compact
              name="providentFundEmployerPercentage"
              label="Employer Contribution (%)"
              type="number"
              onKeyDown={preventSpecialCharKeyDown}
            />
          </div>
          <CheckboxField name="pfWageCeilingApplicable" label="Apply PF wage ceiling (₹15,000)" />
        </ApplicableToggle>

        {/* ESI */}
        <ApplicableToggle
          name="esiApplicable"
          label="Employee State Insurance"
          subtitle="Applies only when gross pay is ₹21,000 a month or less."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 max-w-lg">
            <Input
              compact
              name="esiEmployerPercentage"
              label="Employer Contribution (%)"
              type="number"
              onKeyDown={preventSpecialCharKeyDown}
            />
            <Input
              compact
              name="esiEmployeePercentage"
              label="Employee Contribution (%)"
              type="number"
              onKeyDown={preventSpecialCharKeyDown}
            />
          </div>
        </ApplicableToggle>

        {/* Professional Tax */}
        <ApplicableToggle
          name="profTaxApplicable"
          label="Professional Tax"
          subtitle="State professional tax deducted from salary."
        >
          <div className="max-w-xs">
            <Input
              compact
              name="taxOnProfession"
              label="Professional Tax Amount (₹)"
              type="number"
              onKeyDown={preventSpecialCharKeyDown}
            />
            <HelperText>Capped at ₹2,500 per year.</HelperText>
          </div>
        </ApplicableToggle>

        {/* Noida Professional Tax — kept, disabled while Prof Tax is on */}
        <ApplicableToggle
          name="noidaPtApplicable"
          label="Noida Professional Tax"
          subtitle="Applicable to Noida-based employees."
          disabled={Boolean(profTaxApplicable)}
          disabledReason="Noida Professional Tax cannot be enabled when Professional Tax is applicable."
        />
      </div>
    </SectionCard>
  );
};

export default EmployeeDeductionsSection;
